import { useState } from 'react';
import { FORCE_RULES } from '../shared/q3';
import type { ForceStanding, Standing } from './standings';
import { COH } from './parts';
import { cr, pc } from './format';
import { Link, to } from './router';
import ProductSplit from './ProductSplit';

export type Person = Standing | ForceStanding;

interface Props {
  rows: Standing[];
  force: ForceStanding[];
  /** The signed-in viewer's own row, if we know it. */
  me: Person | null;
  /** Whose progress is on screen: the viewer, or someone they looked up. */
  shown: Person | null;
  pace: number;
  /** "This is me": remember this person as the viewer. */
  onPick: (empId: string) => void;
  /** Show someone else's progress (null = back to the viewer's own). */
  onLookUp: (empId: string | null) => void;
  onNotMe: () => void;
  onSeeOnBoard: (p: Person) => void;
}

const CIRC = 2 * Math.PI * 52;

const isForce = (p: Person): p is ForceStanding => p.cohort === 'Star Force';

/** Name search over every RM and Star Force member. */
function PersonSearch({
  people,
  placeholder,
  autoFocus,
  onSelect,
}: {
  people: Person[];
  placeholder: string;
  autoFocus?: boolean;
  onSelect: (empId: string) => void;
}) {
  const [find, setFind] = useState('');
  const q = find.trim().toLowerCase();
  const hits = q ? people.filter((r) => r.rm.toLowerCase().includes(q)).slice(0, 6) : [];
  return (
    <div className="sk-findbox">
      <input
        className="sk-findin"
        type="search"
        placeholder={placeholder}
        aria-label={placeholder}
        value={find}
        autoFocus={autoFocus}
        onChange={(e) => setFind(e.target.value)}
      />
      {q && (
        <div className="sk-hits">
          {hits.map((r) => (
            <button
              key={r.empId}
              className="sk-hit"
              onClick={() => {
                setFind('');
                onSelect(r.empId);
              }}
            >
              <b>{r.rm}</b>
              <span>{COH[r.cohort].name + (isForce(r) ? ' · ' + r.role : '') + ' · ' + r.loc}</span>
            </button>
          ))}
          {!hits.length && <span className="sk-note">No one by that name on this leaderboard.</span>}
        </div>
      )}
    </div>
  );
}

interface Fact {
  label: string;
  value: string;
  sub: string;
  tone?: 'ok' | 'bad';
}

interface Card {
  /** Big number in the ring: % of target (RMs) or achieved % (Star Force). */
  ringText: string;
  ringLabel: string;
  cohortLine: string;
  ofLine: string;
  need: Fact;
  facts: Fact[];
  hood: Array<{ empId: string; rank: number; ranked: boolean; name: string; gap: string; pct: number }>;
}

/**
 * "Next rank up": the gap to the nearest person strictly ahead. Handles joint
 * leads (shared #1) and people who haven't posted any business yet.
 */
function chaseFact(p: Person, self: boolean, board: string, gap: string): Fact {
  if (!p.ranked) {
    return { label: 'Next rank up', value: 'Get started', sub: 'Any eligible business puts ' + (self ? 'you' : 'them') + ' on the board.' };
  }
  if (!p.chaseName) {
    return p.tied
      ? { label: 'Next rank up', value: '👑 Joint lead', sub: `Tied with ${p.tied} other${p.tied > 1 ? 's' : ''} at the top of ${board}.` }
      : { label: 'Next rank up', value: self ? '👑 You lead' : '👑 Leading', sub: 'Top of ' + board + '. Hold the line.' };
  }
  return { label: 'Next rank up', value: gap, sub: 'to pass ' + p.chaseName + ' for #' + p.chaseRank };
}

/** Two above, two below, clamped to the board's ends. */
function around<T extends { empId: string }>(board: T[], p: T): T[] {
  const i = board.findIndex((r) => r.empId === p.empId);
  const lo = Math.max(0, Math.min(i - 2, board.length - 5));
  return board.slice(lo, lo + 5);
}

/** `self` switches the copy between "you" (own progress) and neutral (someone looked up). */
function rmCard(p: Standing, rows: Standing[], pace: number, self: boolean): Card {
  const C = COH[p.cohort];
  const should = p.targetCr * pace;
  const diff = p.totalCr - should;
  const needSub = p.qualified
    ? (self ? 'Every rupee from here lifts your rank. You are at ' : 'Every rupee from here lifts the rank. At ') +
      pc(p.pct) +
      ' of target.'
    : p.cat2GapCr > 0 && p.totalGapCr > 0
      ? cr(p.totalGapCr) + ' more in total, of which at least ' + cr(p.cat2GapCr) + ' must be Mandatory.'
      : p.cat2GapCr > 0
        ? 'Total target met. Just ' + cr(p.cat2GapCr) + ' more in Mandatory.'
        : 'Mandatory minimum met. ' + cr(p.totalGapCr) + ' more in any eligible product.';
  const board = rows.filter((r) => r.cohort === p.cohort).sort((a, b) => a.rank - b.rank);
  return {
    ringText: pc(p.pct),
    ringLabel: 'of target',
    cohortLine: C.name + ' · ' + p.loc,
    ofLine: 'of ' + p.cohortSize + ' ' + C.name,
    need: {
      label: p.qualified ? 'Status' : self ? 'To qualify you need' : 'To qualify',
      value: p.qualified ? '✓ Qualified' : cr(p.gapCr) + ' more',
      sub: needSub,
    },
    facts: [
      { label: 'Achieved', value: cr(p.totalCr), sub: 'of ' + cr(p.targetCr) + ' target' },
      {
        label: 'Pace check',
        value: (diff >= 0 ? '▲ ' : '▼ ') + cr(Math.abs(diff)),
        sub: (diff >= 0 ? 'ahead of' : 'behind') + " today's pace (" + cr(should) + ')',
        tone: diff >= 0 ? 'ok' : 'bad',
      },
      { label: 'Mandatory', value: cr(p.cat2Cr), sub: pc(p.cat2Pct) + ' of the ' + cr(p.minCr) + ' minimum' },
      chaseFact(p, self, C.name, cr(p.chaseCr)),
    ],
    hood: around(board, p).map((r) => ({
      empId: r.empId,
      rank: r.rank,
      ranked: r.ranked,
      name: r.rm,
      gap: r.qualified ? 'Qualified' : cr(r.gapCr) + ' to go',
      pct: r.pct,
    })),
  };
}

function forceCard(p: ForceStanding, force: ForceStanding[], pace: number, self: boolean): Card {
  // On pace = achieved at least (share of quarter gone) × the 50% bar.
  const should = pace * FORCE_RULES.qualifyAt;
  const diff = p.achieved - should;
  return {
    ringText: pc(p.achieved),
    ringLabel: 'achieved',
    cohortLine: 'Star Force · ' + p.role + ' · ' + p.loc,
    ofLine: 'of ' + p.cohortSize + ' in Star Force',
    need: {
      label: p.qualified ? 'Status' : self ? 'To qualify you need' : 'To qualify',
      value: p.qualified ? '✓ Qualified' : p.needLabel,
      sub: (self ? 'You are at ' : 'At ') + p.progressLabel + '; ' + pc(FORCE_RULES.qualifyAt) + ' qualifies.',
    },
    facts: [
      {
        label: 'Achieved',
        value: pc(p.achieved),
        sub: `goal: ${pc(FORCE_RULES.qualifyAt)}`,
      },
      {
        label: 'Pace check',
        value: (diff >= 0 ? '▲ ' : '▼ ') + Math.round(Math.abs(diff) * 100) + ' pts',
        sub: (diff >= 0 ? 'ahead of' : 'behind') + " today's pace (" + pc(should) + ' achieved)',
        tone: diff >= 0 ? 'ok' : 'bad',
      },
      { label: 'Role', value: p.role === 'IC' ? 'IC' : 'Acquisition', sub: 'Star Force' },
      chaseFact(p, self, 'Star Force', Math.round(p.chasePct * 100) + ' pts'),
    ],
    hood: around(force, p).map((r) => ({
      empId: r.empId,
      rank: r.rank,
      ranked: r.ranked,
      name: r.rm,
      gap: r.qualified ? 'Qualified' : r.needLabel,
      pct: r.achieved,
    })),
  };
}

function greeting(p: Person, pace: number, self: boolean): string {
  if (self) return p.qualified ? 'Qualified · keep climbing' : p.pct >= pace ? 'On pace · keep going' : 'Time to push';
  return 'Looking up · ' + (p.qualified ? 'Qualified' : p.pct >= pace ? 'On pace' : 'Behind pace');
}

export default function MyProgressView({ rows, force, me, shown, pace, onPick, onLookUp, onNotMe, onSeeOnBoard }: Props) {
  const people: Person[] = [...rows, ...force];
  const self = !!shown && !!me && shown.empId === me.empId;
  const C = shown ? COH[shown.cohort] : null;
  const card = shown ? (isForce(shown) ? forceCard(shown, force, pace, self) : rmCard(shown, rows, pace, self)) : null;

  return (
    <div className="sk-view sk-mine" id="my-progress">
      <nav className="sk-mine-nav" aria-label="Back">
        <Link className="sk-back" to={to('home')}>
          ← Overview
        </Link>
      </nav>
      <div className="sk-h2row sk-mine-h">
        <div>
          <div className="sk-num">{self || !shown ? 'MY PROGRESS' : 'PROGRESS'}</div>
          <h2>{self || !shown ? 'Where you stand' : 'Where ' + shown.rm.split(' ')[0] + ' stands'}</h2>
        </div>
        <p className="sk-note">Rank, pace and exactly what's left</p>
      </div>
      {shown && (
        <div className="sk-lookup">
          <PersonSearch people={people} placeholder="Look up any RM or Star Force member" onSelect={(id) => onLookUp(id)} />
        </div>
      )}
      <div className={'sk-me' + (C ? ' ' + C.cls : '')}>
        {!shown || !C || !card ? (
          <div className="sk-find">
            <div>
              <div className="sk-eyebrow">My progress</div>
              <h3>Find yourself on the board</h3>
              <p>
                Search anyone on the leaderboard. Pick your own name once and your rank and what you need to qualify are
                right here next time.
              </p>
            </div>
            <PersonSearch people={people} placeholder="Type a name" autoFocus onSelect={onPick} />
          </div>
        ) : (
          <div className="sk-me-grid">
            <div className="sk-ring2">
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <defs>
                  <linearGradient id="skRingG" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#f3dc92" />
                    <stop offset=".5" stopColor="#ef7a1a" />
                    <stop offset="1" stopColor="#d8261a" />
                  </linearGradient>
                </defs>
                <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="10" />
                {shown.pct > 0 && (
                  <circle
                    className="sk-ringv"
                    cx="60"
                    cy="60"
                    r="52"
                    fill="none"
                    stroke="url(#skRingG)"
                    strokeWidth="10"
                    // Round caps would turn a sliver of progress into a dot; keep short arcs flat.
                    strokeLinecap={Math.min(shown.pct, 1) * CIRC < 12 ? 'butt' : 'round'}
                    strokeDasharray={`${Math.min(shown.pct, 1) * CIRC} ${CIRC}`}
                  />
                )}
                <circle
                  className="sk-ringp"
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="rgba(245,240,232,.9)"
                  strokeWidth="14"
                  strokeDasharray="1.2 999"
                  strokeDashoffset={String(-(pace * CIRC))}
                />
              </svg>
              <div className="c">
                <div className="p">{card.ringText}</div>
                <div className="s">{card.ringLabel}</div>
              </div>
            </div>
            <div className="sk-me-id">
              <div className="hi">{greeting(shown, pace, self)}</div>
              <div className="nm">{shown.rm}</div>
              <div className="coh">
                <span className={'th ' + C.em} />
                <span>{card.cohortLine}</span>
              </div>
              <div className="sk-rankbig">
                <span className="r sk-goldtext">{shown.ranked ? '#' + shown.rank : '–'}</span>
                <span className="of">{card.ofLine}</span>
              </div>
              <div className="sk-me-acts">
                <button className="sk-btn" onClick={() => onSeeOnBoard(shown)}>
                  {self ? 'See me on the leaderboard →' : 'See on the leaderboard →'}
                </button>
                {self ? (
                  <button className="sk-btn ghost" onClick={onNotMe}>
                    Not you?
                  </button>
                ) : me ? (
                  <button className="sk-btn ghost" onClick={() => onLookUp(null)}>
                    ← Back to my progress
                  </button>
                ) : (
                  <button className="sk-btn ghost" onClick={() => onPick(shown.empId)}>
                    This is me
                  </button>
                )}
              </div>
            </div>
            <div className="sk-me-facts">
              <div className={'sk-fact need' + (shown.qualified ? ' done' : '')}>
                <div className="l">{card.need.label}</div>
                <div className="v">{card.need.value}</div>
                <div className="s">{card.need.sub}</div>
              </div>
              {card.facts.map((f) => (
                <div className="sk-fact" key={f.label}>
                  <div className="l">{f.label}</div>
                  <div className={'v' + (f.tone ? ' ' + f.tone : '')}>{f.value}</div>
                  <div className="s">{f.sub}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {shown && !isForce(shown) && (
        <div className="sk-sec">
          <div className="sk-h2row">
            <div>
              <div className="sk-num">PRODUCT SPLIT</div>
              <h2>Business by product</h2>
            </div>
            <p className="sk-note">Where {self ? 'your' : shown.rm.split(' ')[0] + "'s"} business comes from</p>
          </div>
          <div className="sk-glass sk-split-card">
            <ProductSplit p={shown} />
          </div>
        </div>
      )}
      {shown && card && (
        <div className="sk-sec sk-hood-wrap">
          <div className="sk-h2row">
            <div>
              <div className="sk-num">THE NEIGHBOURHOOD</div>
              <h2>{isForce(shown) ? 'The members around' : 'The RMs around'}</h2>
            </div>
            <p className="sk-note">Who's just ahead, who's right behind · tap a name to look them up</p>
          </div>
          <div className="sk-glass sk-hood">
            {card.hood.map((r) => {
              const isShown = r.empId === shown.empId;
              const isMe = !!me && r.empId === me.empId;
              return (
                <div
                  key={r.empId}
                  className={'rw' + (isShown ? ' me' : '') + (isShown ? '' : ' sk-hood-link')}
                  role={isShown ? undefined : 'button'}
                  tabIndex={isShown ? undefined : 0}
                  onClick={isShown ? undefined : () => onLookUp(r.empId)}
                  onKeyDown={isShown ? undefined : (e) => (e.key === 'Enter' || e.key === ' ') && onLookUp(r.empId)}
                >
                  <div className="rk">{r.ranked ? '#' + r.rank : '–'}</div>
                  <div>
                    {r.name}
                    {isMe && <span className="sk-you">YOU</span>}
                  </div>
                  <div className="gp">{r.gap}</div>
                  <div className="pc">{pc(r.pct)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
